"use client";

import Link from "next/link";
import { useState } from "react";
import { IssueDialog } from "@/components/issue-dialog";
import { Banner, Button, Card, ErrorBox, Spinner } from "@/components/ui";
import { call, client } from "@/lib/api";
import { useAsync } from "@/lib/hooks";
import { useT } from "@/lib/i18n";
import type { Key } from "@/i18n/id";

export default function DashboardPage() {
  const t = useT();
  const stats = useAsync(() => call(client.GET("/api/stats")));
  const settings = useAsync(() => call(client.GET("/api/settings")));
  const [issueOpen, setIssueOpen] = useState(false);

  if (stats.error) return <ErrorBox error={stats.error} />;
  const s = stats.data;

  // The admin's day, in order. Counts show where work is waiting right now.
  const steps: { key: string; who: "admin" | "both"; href: string | null; waiting: number }[] = [
    { key: "type", who: "admin", href: "/types/new", waiting: 0 },
    { key: "issue", who: "admin", href: null, waiting: 0 },
    { key: "print", who: "both", href: "/factory", waiting: s?.awaiting_label ?? 0 },
    { key: "confirm", who: "both", href: "/factory", waiting: s?.awaiting_confirmation ?? 0 },
    { key: "ship", who: "admin", href: "/units?status=label_fixed", waiting: s?.by_status.label_fixed ?? 0 },
    { key: "handover", who: "admin", href: "/handover", waiting: s?.not_exported ?? 0 },
  ];

  const tiles = s
    ? [
        { href: "/units?status=issued", n: s.awaiting_label, label: t("dash.awaiting_label"), warn: s.awaiting_label > 0 },
        { href: "/units?status=label_printed", n: s.awaiting_confirmation, label: t("dash.awaiting_confirmation"), warn: s.awaiting_confirmation > 0 },
        { href: "/units?status=label_fixed", n: s.by_status.label_fixed ?? 0, label: t("dash.ready_to_ship") },
        { href: "/units?status=shipped", n: s.by_status.shipped ?? 0, label: t("dash.shipped") },
        { href: "/units?missing_spec=1", n: s.missing_spec, label: t("dash.missing_spec"), warn: s.missing_spec > 0 },
        { href: "/handover", n: s.not_exported, label: t("dash.not_exported"), warn: s.not_exported > 0 },
        { href: "/growth", n: s.reconcile_issues, label: t("dash.reconcile"), warn: s.reconcile_issues > 0 },
      ]
    : [];

  return (
    <div className="stack">
      <div className="row-between">
        <h1 style={{ margin: 0 }}>{t("dash.title")}</h1>
        <Button variant="primary" onClick={() => setIssueOpen(true)}>
          ＋ {t("units.issue")}
        </Button>
      </div>

      {settings.data?.activation_is_placeholder && (
        <Banner kind="warn">
          {t("dash.placeholder_warn")} <Link href="/settings">{t("nav.settings")}</Link>
        </Banner>
      )}

      {!s ? (
        <Spinner />
      ) : (
        <div className="grid grid-3">
          {tiles.map((x) => (
            <Link key={x.href + x.label} href={x.href} className={`stat${x.warn ? " stat-warn" : ""}`}>
              <span className="stat-n">{x.n}</span>
              <span>{x.label}</span>
            </Link>
          ))}
        </div>
      )}

      <Card title={t("dash.flow_title")}>
        <p className="muted small">{t("dash.flow_intro")}</p>
        <ol className="steps">
          {steps.map((st, i) => (
            <li key={st.key} className="step">
              <span className="step-n">{i + 1}</span>
              <div>
                <b>{t(`dash.step.${st.key}` as Key)}</b>{" "}
                <span className="badge">{t(st.who === "admin" ? "role.admin" : "dash.who.both")}</span>
                {st.waiting > 0 && <span className="badge badge-label_printed" style={{ marginLeft: "0.4rem" }}>{t("dash.waiting", { n: st.waiting })}</span>}
                <div className="muted small">{t(`dash.step.${st.key}_desc` as Key)}</div>
              </div>
              <div className="step-action">
                {st.href ? (
                  <Link className="btn btn-sm" href={st.href}>
                    {t("dash.open")}
                  </Link>
                ) : (
                  <Button size="sm" onClick={() => setIssueOpen(true)}>
                    {t("dash.open")}
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ol>
      </Card>

      <IssueDialog open={issueOpen} onClose={() => setIssueOpen(false)} onIssued={() => stats.reload()} />
    </div>
  );
}
